import React from "react";
import { Button, TextField } from "components";
import formatDate from "utils/formatDate";
import { isValidDate } from "utils/validDate";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { usePropertyContext } from "../../context";
import moment from "moment";
import ButtonV2 from "elements/ButtonV2";

const Dob = () => {
	const { flowId, next, dobValue, setDobValue, specialFieldsValue } =
		useExemptionsContext();
	const { details } = usePropertyContext();
	const trackEvent = useEventTracker();

	const handleSubmit = async () => {
		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_DOB_Answer,
			data: {
				"Date of Birth": dobValue,
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
			},
		});
		const trackingEventData = {
			"Date of Birth": dobValue,
		};
		next({ trackingEventData });
	};

	const ageDisqualifies = React.useMemo(() => {
		if (!dobValue || dobValue.length !== 10) return false;

		const age = moment().diff(dobValue, "years");

		if (flowId === "GEORGIA_EXEMPTION") {
			return age >= 62;
		}

		if (flowId === "ILLINOIS_EXEMPTION") {
			return age >= 65;
		}

		if (flowId === "FLORIDA_EXEMPTION") {
			return age >= 65;
		}

		if (flowId === "NEW_YORK_EXEMPTION") {
			return (
				age >= 65 &&
				specialFieldsValue?.income_tax_history?.[0]?.annual_household_income &&
				specialFieldsValue?.income_tax_history?.[0]?.annual_household_income <
					93_200
			);
		}

		return false;
	}, [dobValue]);

	const warningMessage = React.useMemo(() => {
		if (ageDisqualifies) {
			if (flowId === "NEW_YORK_EXEMPTION") {
				return "At the moment Ownwell is unable to support this type of exemption or STAR Credit filing in your county. While we're not able to help you with your exemption or STAR Credit application, you can still file yourself directly with the county through the county website.";
			}
			if (flowId === "FLORIDA_EXEMPTION") {
				return "At this time, we are unable to support 65 year or older senior exemptions in Florida. However, you can still file your exemption with the county directly at the county website.";
			}
			if (flowId === "ILLINOIS_EXEMPTION") {
				return "We currently do not support Senior Exemption filing in your county at this time. However, you can still file the exemption yourself to ensure you receive your Senior Exemption by visiting your County Appraisal District website. There it will explain the steps you need to take. You can proceed on to the next step and we will determine if there are other ways we can help you save!";
			}
			return "At the moment Ownwell is unable to support this type of Exemption filing in your county. While we're not able to help you with your Exemptions application, you can still file yourself directly with the county through the county website.";
		}

		return null;
	}, [flowId, ageDisqualifies]);

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				<div className="property-profile-question-card-title-container">
					<p>Date of Birth</p>
				</div>
				{!["GEORGIA_EXEMPTION", "FLORIDA_EXEMPTION"].includes(flowId ?? "") && (
					<p className="exemption-question-card-subtitle sm">
						There are different types of exemptions available depending on your
						age.
					</p>
				)}
				<TextField
					required
					label="Date of Birth"
					placeholder="MM/DD/YYYY"
					containerClassName="exemptions-card-input"
					maxLength={10}
					minLength={10}
					value={dobValue ?? ""}
					onChange={e => {
						const formatted = formatDate(e, dobValue ?? "");
						const valid = isValidDate(formatted);

						if (valid) {
							setDobValue(formatted);
						}
					}}
				/>
			</div>
			{warningMessage && (
				<p className="sm exemptions-notification-message-rust">
					{warningMessage}
				</p>
			)}
			<ButtonV2
				size="large"
				disabled={dobValue === undefined || dobValue?.length !== 10}
				type="submit">
				Continue
			</ButtonV2>
		</form>
	);
};

export default Dob;
